import { useState } from "react";
import { Play, ChevronLeft, ChevronRight } from "lucide-react";
import { useContent } from "../../context/ContentContext";
import { Reveal } from "./Reveal";
import { useShowreel } from "./Showreel";
import { SectionBg } from "./SectionBg";
import { FramedImage } from "./FramedImage";
import { boxEffectStyle } from "../../lib/frame";
import { mediaItems } from "../../lib/media";

// Media for one activity block. Priority: video > multiple photos (carousel) > single photo.
// Frame dimensions/ratio/rounding are unchanged; multiple photos scroll INSIDE the same frame.
// Each photo keeps its OWN saved frame (X/Y/Zoom); subtle legibility effects come from the admin.
const ActivityMedia = ({ item, i, eyebrow, open }) => {
  const [idx, setIdx] = useState(0);
  const fmt = item.imageFrame?.format;
  const frameSize = fmt === "vertical" ? "aspect-[2/3]" : fmt === "square" ? "aspect-square" : fmt === "horizontal" ? "aspect-video" : "h-[56vw] lg:h-[30rem]";
  const frame = `group relative block w-full overflow-hidden rounded-lg ${frameSize}`;
  const fx = item.effects;
  const boxStyle = boxEffectStyle(fx);
  const hasVideo = item.videoUrl && item.videoUrl.trim() !== "";
  // Slides = main image (imageFrame) + additional photos (each carries its own frame).
  const slides = [];
  if (item.image) slides.push({ url: item.image, frame: item.imageFrame });
  mediaItems(item.images).forEach((m) => { if (m.url) slides.push({ url: m.url, frame: m.frame }); });
  const thumb = slides[0]?.url || "";

  if (hasVideo) {
    return (
      <button data-testid={`activity-media-${i}`} onClick={() => open(item.videoUrl, item.title, eyebrow)} className={frame} style={boxStyle}>
        <div className="w-full h-full transition-transform duration-[1.2s] ease-out group-hover:scale-110"><FramedImage src={thumb} frame={slides[0]?.frame} effects={fx} alt={item.title} /></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/70 to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <div className="w-16 h-16 rounded-full border border-electric/70 backdrop-blur-sm flex items-center justify-center bg-black/30"><Play size={20} className="fill-electric text-electric ml-0.5" /></div>
        </div>
        <span className="absolute top-4 left-4 text-[10px] tracking-[0.25em] uppercase text-electric bg-black/40 backdrop-blur px-3 py-1 rounded-full border border-electric/40">Vidéo</span>
      </button>
    );
  }

  const multi = slides.length > 1;
  const cur = slides.length ? slides[idx % slides.length] : null;
  const go = (e, d) => { e.stopPropagation(); setIdx((p) => (p + d + slides.length) % slides.length); };
  return (
    <div data-testid={`activity-media-${i}`} className={`${frame} bg-[#0e0e0e]`} style={boxStyle}>
      {cur ? <FramedImage src={cur.url} frame={cur.frame} effects={fx} alt={item.title} /> : <div className="w-full h-full" />}
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/70 to-transparent pointer-events-none" />
      {multi && (
        <>
          <button data-testid={`activity-prev-${i}`} onClick={(e) => go(e, -1)} aria-label="Photo précédente" className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/50 backdrop-blur border border-white/20 text-white flex items-center justify-center hover:border-electric hover:text-electric transition-colors"><ChevronLeft size={18} /></button>
          <button data-testid={`activity-next-${i}`} onClick={(e) => go(e, 1)} aria-label="Photo suivante" className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/50 backdrop-blur border border-white/20 text-white flex items-center justify-center hover:border-electric hover:text-electric transition-colors"><ChevronRight size={18} /></button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
            {slides.map((_, p) => <span key={p} className={`h-1.5 rounded-full transition-all ${p === (idx % slides.length) ? "w-5 bg-electric" : "w-1.5 bg-white/40"}`} />)}
          </div>
        </>
      )}
    </div>
  );
};

export const Activities = () => {
  const { content } = useContent();
  const a = content.activities;
  const { open } = useShowreel();

  return (
    <section id="activites" className="relative overflow-hidden bg-[#050505] py-24 md:py-40 border-t border-white/10" data-testid="activities-section">
      <SectionBg id="activities" />
      <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12">
        <Reveal className="mb-16 md:mb-28 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-electric" />
              <span className="text-xs tracking-[0.35em] uppercase text-electric">{a.eyebrow}</span>
            </div>
            <h2 className="font-serif text-5xl md:text-7xl text-white tracking-tight leading-[0.95]">{a.heading}</h2>
          </div>
          <p className="max-w-sm text-sm text-secondary2 font-light leading-relaxed">{a.intro}</p>
        </Reveal>

        <div className="space-y-16 md:space-y-28">
          {a.items.filter((item) => item.enabled !== false).map((item, i) => {
            const even = i % 2 === 0;
            return (
              <Reveal key={item.key || i}>
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center ${even ? "" : "lg:[direction:rtl]"}`}>
                  <div className="lg:[direction:ltr]">
                    <ActivityMedia item={item} i={i} eyebrow={a.eyebrow} open={open} />
                  </div>

                  <div className="lg:[direction:ltr]">
                    <span className="block h-px w-10 bg-electric mb-6" />
                    <h3 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-5">{item.title}</h3>
                    <p className="text-base text-secondary2 font-light leading-relaxed max-w-lg">{item.description}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
